import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Container,
  alpha,
  useTheme,
  Chip,
  IconButton,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getMyQuizzes, getStudentQuizzes, clearQuiz, deleteQuiz } from "../../state/slices/quiz";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { Edit, Delete, PlayArrow, Visibility, CheckCircle, Quiz, ArrowBack } from "@mui/icons-material";

const MyQuizzes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const { quizzes, loading, errorMsg } = useSelector((state) => state.quiz);
  const user = useSelector((state) => state.user.user);
  const isStudentView = location.pathname === '/quiz/student';
  const isTutor = user?.roles?.includes("TUTOR");
  
  // Root navigation paths - don't show back button on these
  const rootPaths = ['/quiz/my', '/quiz/student', '/flashcard/my', '/flashcard/student', '/progress', '/', '/request', '/student-request', '/tutor-class-room', '/student-class-room'];
  const showBackButton = !rootPaths.includes(location.pathname);

  useEffect(() => {
    if (isStudentView) {
      dispatch(getStudentQuizzes());
    } else {
      dispatch(getMyQuizzes());
    }
  }, [dispatch, isStudentView]);

  const handleDelete = (quizId) => {
    if (window.confirm(t('quiz.delete') + "?")) {
      dispatch(deleteQuiz(quizId)).then(() => {
        if (isStudentView) {
          dispatch(getStudentQuizzes());
        } else {
          dispatch(getMyQuizzes());
        }
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Back Button - only show if not on root navigation page */}
        {showBackButton && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {t('common.back')}
          </Button>
        )}

        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Quiz sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 0.5,
                  }}
                >
                  {isStudentView ? t('quiz.assignedQuizzes') : t('quiz.myQuizzes')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isStudentView 
                    ? t('quiz.assignedQuizzesDesc') || 'View and take quizzes assigned to you'
                    : t('quiz.myQuizzesDesc') || 'Create and manage your quizzes'}
                </Typography>
              </Box>
            </Box>
            {!isStudentView && (
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => navigate("/quiz/create")}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                {t('quiz.createNewQuiz')}
              </Button>
            )}
          </Box>
        </Box>

        {quizzes.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Quiz sx={{ fontSize: 64, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
            <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
              {isStudentView ? t('quiz.noQuizzesAvailable') : t('quiz.noQuizzes')}
            </Typography>
            {!isStudentView && (
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => navigate("/quiz/create")}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                {t('quiz.createNewQuiz')}
              </Button>
            )}
          </Card>
        ) : (
          <Grid container spacing={3}>
            {quizzes.map((quiz) => (
              <Grid item xs={12} sm={6} md={4} key={quiz._id}>
                <Card
                  elevation={0}
                  onClick={() => {
                    // Only navigate if view button would be shown
                    if (!isTutor || quiz.createdBy?._id === user?.id || quiz.createdBy === user?.id) {
                      navigate(`/quiz/${quiz._id}`);
                    }
                  }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    cursor: (!isTutor || quiz.createdBy?._id === user?.id || quiz.createdBy === user?.id) ? 'pointer' : 'default',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Quiz sx={{ fontSize: 24 }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            mb: 0.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {quiz.title}
                        </Typography>
                        {/* Show creator info for student view */}
                        {isStudentView && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {quiz.createdBy?._id === user?.id || quiz.createdBy === user?.id 
                              ? t('quiz.createdByMe') 
                              : t('quiz.createdBy') + ` ${quiz.createdBy?.firstName || ''} ${quiz.createdBy?.lastName || ''}`.trim()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        flexGrow: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {quiz.description || t('quiz.noDescription')}
                    </Typography>
                    
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {quiz.subject && (
                        <Chip 
                          label={quiz.subject} 
                          size="small" 
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                          }} 
                        />
                      )}
                      <Chip 
                        label={quiz.status.charAt(0).toUpperCase() + quiz.status.slice(1)} 
                        size="small" 
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                      {isStudentView && quiz.isCompleted && (
                        <Chip 
                          icon={<CheckCircle />}
                          label={t('quiz.completed')} 
                          size="small" 
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    
                    <Box 
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        {quiz.questions?.length || 0} {t('quiz.questions')} • {quiz.totalPoints} {t('quiz.points')}
                      </Typography>
                    </Box>
                    
                    <Box 
                      sx={{ display: "flex", gap: 1, justifyContent: 'flex-end' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Only show view button for students/parents, or for tutors if they created it */}
                      {(!isTutor || quiz.createdBy?._id === user?.id || quiz.createdBy === user?.id) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/quiz/${quiz._id}`);
                          }}
                          title={t('quiz.view')}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      )}
                      {isStudentView && quiz.isCompleted && quiz.completedAttemptId && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/quiz/results/${quiz.completedAttemptId}`);
                          }}
                          title={t('quiz.viewResults')}
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.success.main, 0.2),
                            },
                          }}
                        >
                          <CheckCircle />
                        </IconButton>
                      )}
                      {/* Only show edit/delete if user is the creator (tutor) or if it's their own quiz */}
                      {(isTutor || !isStudentView || quiz.createdBy?._id === user?.id) && (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/quiz/create`, { state: { quiz } });
                            }} 
                            title={t('quiz.edit')}
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.info.main, 0.2),
                              },
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(quiz._id);
                            }}
                            title={t('quiz.delete')}
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.2),
                              },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default MyQuizzes;

